<template>
  <div class="relative">
    <div
      ref="slider"
      class="keen-slider"
      :class="classes"
    >
      <slot />
    </div>

    <template v-if="slider && hasArrows">
      <button
        aria-label="Previous"
        :class="{
          'keen-slider__arrow': true,
          'keen-slider__arrow--left': true,
          'keen-slider__arrow--disabled': current === 0,
        }"
        @click="slider.prev()"
      >
        <span class="icon-circle-arrow-left" />
      </button>

      <button
        aria-label="Next"
        :class="{
          'keen-slider__arrow': true,
          'keen-slider__arrow--right': true,
          'keen-slider__arrow--disabled': current === 0,
        }"
        @click="slider.next()"
      >
        <span class="icon-circle-arrow-right" />
      </button>
    </template>

    <div
      v-if="slider && hasPagination"
      class="keen-slider__pagination"
    >
      <button
        v-for="(_slide, idx) in dotHelper"
        :key="idx"
        class="rounded-full"
        :aria-label="'Go to slide ' + (idx + 1)"
        :class="{ 'dot': true, 'dot--active': current === idx }"
        @click="slider.moveToIdx(idx)"
      />
    </div>

    <div
      v-if="slider && hasProgressBar"
      class="keen-slider__progress"
      role="progressbar"
      :aria-valuemin="0"
      :aria-valuemax="100"
      :aria-valuenow="Math.round(progressPercentage)"
    >
      <span
        class="keen-slider__progress-fill"
        :style="{ width: progressPercentage + '%' }"
      />
    </div>
  </div>
</template>

<script>
/*
* Keen Slider Vue component
* ---
* https://keen-slider.io/docs
*
* Example usage:
* <keen-slider
*   :arrows="true"
*   pagination="(max-width: 767px)"
*   :autoplay="3000"
*   :options="{
*     slides: {
*       perPage: 2,
*       spacing: 15
*     },
*     breakpoints: {
*       '(min-width: 768px)': {
*         slides: {
*           perView: 4,
*           spacing: 30
*         }
*       }
*     }
*   }"
* >...</keen-slider>
*/

import KeenSlider from 'keen-slider'

export default {
  props: {
    classes: {
      type: String,
      default: ''
    },

    options: {
      type: Object,
      default: () => {}
    },

    autoplay: {
      type: Number,
      default: 0
    },

    arrows: {
      type: [Boolean, String],
      default: false
    },

    pagination: {
      type: [Boolean, String],
      default: false
    },

    progressBar: {
      type: [Boolean, String],
      default: false
    },

    scrollNavigation: {
      type: Boolean,
      default: false
    },

    cropPagination: {
      type: Boolean,
      default: false
    },

    increasePaginationGap: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    slider: null,
    current: 0,
    hasArrows: false,
    hasPagination: false,
    hasProgressBar: false
  }),

  computed: {
    dotHelper () {
      return this.slider ? [...Array(this.slider.track.details.slides.length).keys()] : []
    },

    totalSlides () {
      return this.slider ? this.slider.track.details.slides.length : 0
    },

    progressPercentage () {
      if (!this.slider) return 0

      const details = this.slider.track.details
      if (!details) return 0

      // Use the reachable index range so progress hits 100% at the actual end,
      // including fractional perView (e.g. 2.5/4.5).
      const maxIdx = Number(details.maxIdx || 0)
      if (maxIdx <= 0) return 100

      const rel = Number(details.rel || 0)
      const totalSlides = Number(this.totalSlides || 0)
      if (!totalSlides) return 0

      // Starting progress reflects fully visible slides at index 0.
      // Example: perView 4.5 of 10 slides -> floor(4.5) / 10 = 40%.
      const perView = totalSlides - maxIdx
      const initialVisibleSlides = Math.max(1, Math.floor(perView))
      const initialProgress = Math.min((initialVisibleSlides / totalSlides) * 100, 100)

      const progress = initialProgress + ((100 - initialProgress) * rel / maxIdx)
      return Math.min(Math.max(progress, initialProgress), 100)
    }
  },

  mounted () {
    for (const slot of this.$slots.default) {
      slot.elm.classList.add('keen-slider__slide')
    }

    this.$nextTick(() => {
      this.slider = new KeenSlider(this.$refs.slider, {
        initial: this.current,
        loop: true,
        ...this.options,
        slideChanged: (s) => {
          this.current = s.track.details.rel
        }
      }, [
        this.autoSwitch,
        this.useScrollWheel // allow use of mousewheel or trackpad to move the slider items
      ])

      if (typeof this.arrows === 'boolean') {
        this.hasArrows = this.arrows
      } else {
        const arrowMediaQuery = window.matchMedia(this.arrows)
        this.hasArrows = arrowMediaQuery.matches
        arrowMediaQuery.addEventListener('change', (mq) => {
          this.hasArrows = mq.matches
        })
      }

      if (typeof this.pagination === 'boolean') {
        this.hasPagination = this.pagination
      } else {
        const paginationMediaQuery = window.matchMedia(this.pagination)
        this.hasPagination = paginationMediaQuery.matches
        paginationMediaQuery.addEventListener('change', (mq) => {
          this.hasPagination = mq.matches
        })
      }

      if (typeof this.progressBar === 'boolean') {
        this.hasProgressBar = this.progressBar
      } else {
        const progressBarMediaQuery = window.matchMedia(this.progressBar)
        this.hasProgressBar = progressBarMediaQuery.matches
        progressBarMediaQuery.addEventListener('change', (mq) => {
          this.hasProgressBar = mq.matches
        })
      }
    })
  },

  beforeUnmount () {
    if (this.slider) this.slider.destroy()
  },

  // beforeDestroy () {
  //   if (this.slider) this.slider.destroy()
  // },

  methods: {
    autoSwitch (slider) {
      if (!this.autoplay) return false

      const wait = this.autoplay

      let timeout
      let mouseOver = false

      function clearNextTimeout () {
        clearTimeout(timeout)
      }

      function nextTimeout () {
        clearTimeout(timeout)
        if (mouseOver) return

        timeout = setTimeout(() => {
          slider.next()
        }, wait)
      }

      slider.on('created', () => {
        slider.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearNextTimeout()
        })
        slider.container.addEventListener('mouseout', () => {
          mouseOver = false
          nextTimeout()
        })
        nextTimeout()
      })

      slider.on('dragStarted', clearNextTimeout)
      slider.on('animationEnded', nextTimeout)
      slider.on('updated', nextTimeout)
    },

    useScrollWheel (slider) {
      if (!this.scrollNavigation) return false
      let touchTimeout
      let position
      let wheelActive

      const dispatch = (e, name) => {
        position.x -= e.deltaX
        position.y -= e.deltaY
        slider.container.dispatchEvent(
          new CustomEvent(name, {
            detail: {
              x: position.x,
              y: position.y
            }
          })
        )
      }

      const wheelStart = (e) => {
        position = {
          x: e.pageX,
          y: e.pageY
        }
        dispatch(e, 'ksDragStart')
      }

      const wheel = (e) => {
        dispatch(e, 'ksDrag')
      }

      const wheelEnd = (e) => {
        dispatch(e, 'ksDragEnd')
      }

      const eventWheel = (e) => {
        e.preventDefault()
        if (!wheelActive) {
          wheelStart(e)
          wheelActive = true
        }
        wheel(e)
        clearTimeout(touchTimeout)
        touchTimeout = setTimeout(() => {
          wheelActive = false
          wheelEnd(e)
        }, 50)
      }

      slider.on('created', () => {
        slider.container.addEventListener('wheel', eventWheel, {
          passive: true
        })
      })
    }
  }

}

</script>
